import { z } from 'zod';
import { ValidationPatterns } from '../shared';

/**
 * TOKEN SCHEMAS - Learning Session Authentication
 * =============================================================================
 * Following Base+Variants pattern from docs/schemas-plan.md
 * Manages authentication tokens for webview learning sessions
 * Used by TokenService and learning routes
 * =============================================================================
 */

// =============================================================================
// BASE TOKEN SCHEMA - Learning session tokens (8 fields)
// =============================================================================
const BaseTokenSchema = z.object({
  // Core identity
  id: ValidationPatterns.flexibleObjectId, // Token document identifier
  token: ValidationPatterns.uuid, // Unique token string

  // Entity relationships
  userId: ValidationPatterns.flexibleObjectId, // User who owns this token
  wordCollectionId: ValidationPatterns.flexibleObjectId.optional(), // Optional collection context

  // Token state
  used: z.boolean(), // Whether token has been consumed
  sessionStarted: z.boolean(), // Whether learning session has started
  expiresAt: ValidationPatterns.flexibleDate.optional(), // Optional expiration date

  // System timestamps
  createdAt: ValidationPatterns.flexibleDate,
});

// =============================================================================
// TOKEN VARIANTS - For different usage contexts
// =============================================================================
export const TokenSchemas = {
  /**
   * FULL: Complete token entity (service layer)
   * All 8 fields for internal operations
   */
  full: BaseTokenSchema,

  /**
   * CREATE: Input schema for creating new tokens
   * Omits system-generated fields like id and timestamps
   */
  create: BaseTokenSchema.omit({
    id: true,
    createdAt: true,
  }),

  /**
   * UPDATE: Input schema for updating existing tokens
   * Partial schema allowing selective field updates, omitting system fields
   */
  update: BaseTokenSchema.omit({
    id: true,
    token: true,
    createdAt: true,
  }).partial(),

  /**
   * VALIDATION: Essential fields for token validation
   * Used for authentication and session management
   */
  validation: BaseTokenSchema.pick({
    id: true,
    token: true,
    userId: true,
    wordCollectionId: true,
    used: true,
    sessionStarted: true,
    expiresAt: true,
    createdAt: true,
  }),

  /**
   * PUBLIC: Fields safe for client-side exposure
   * Never expose internal token strings or sensitive data
   */
  public: BaseTokenSchema.pick({
    id: true,
    userId: true,
    wordCollectionId: true,
    sessionStarted: true,
    expiresAt: true,
  }),
} as const;

// =============================================================================
// TYPE EXPORTS - Clean TypeScript types
// =============================================================================
export type Token = z.infer<typeof BaseTokenSchema>;
export type CreateToken = z.infer<typeof TokenSchemas.create>;
export type UpdateToken = z.infer<typeof TokenSchemas.update>;
export type ValidationToken = z.infer<typeof TokenSchemas.validation>;
export type PublicToken = z.infer<typeof TokenSchemas.public>;
